import { useId, type ReactNode, type SVGProps } from "react";
import { cn } from "~/lib/utils";
import "./manifold-nextgen-nav-icons.css";

/**
 * The nav-picker's section icons, drawn so that one part of each can move.
 * Motion lives in the stylesheet, keyed off an enclosing `NAV_ICON_HOST_CLASS`.
 */

/** Marks the element whose hover/focus drives the icons nested inside it. */
export const NAV_ICON_HOST_CLASS = "nav-icon-host";

interface NavIconProps {
  className?: string;
}

const LUCIDE_ATTRS = {
  xmlns: "http://www.w3.org/2000/svg",
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
} as const satisfies SVGProps<SVGSVGElement>;

function NavIconSvg({ className, children }: NavIconProps & { children: ReactNode }) {
  return (
    <svg {...LUCIDE_ATTRS} className={cn("nav-icon", className)}>
      {children}
    </svg>
  );
}

/** The door opens: its right edge slides inward, widening into a gap. */
export function HomeNavIcon({ className }: NavIconProps) {
  return (
    <NavIconSvg className={className}>
      <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
      {/* Parked on the door's right jamb, so it is invisible until it moves. */}
      <path className="nav-icon-door-edge" d="M15 21v-8" />
    </NavIconSvg>
  );
}

/** The cover slides off the pages. */
export function ProjectsNavIcon({ className }: NavIconProps) {
  return (
    <NavIconSvg className={className}>
      <path d="M2.4 6h3.6" />
      <path d="M2.4 10h3.6" />
      <path d="M2.4 14h3.6" />
      <path d="M2.4 18h3.6" />
      <rect width="16" height="20" x="4" y="2" rx="2" />
      <path className="nav-icon-cover" d="M16 2v20" />
    </NavIconSvg>
  );
}

/** A filed page rises out of the folder. */
export function DatasetsNavIcon({ className }: NavIconProps) {
  // `useId` output carries delimiters that are unsafe inside a `url(#…)`.
  const clipPathId = `nav-icon-folder-${useId().replace(/[^\w-]/g, "")}`;
  return (
    <NavIconSvg className={className}>
      {/* Clipped above the folder's front edge, so the page rises out of it. */}
      <clipPath id={clipPathId}>
        <rect x="10.5" y="0" width="12" height="6" />
      </clipPath>
      <g clipPath={`url(#${clipPathId})`}>
        <rect className="nav-icon-page" x="12.5" y="2.5" width="7" height="7" rx="1" />
      </g>
      <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    </NavIconSvg>
  );
}

/** The bot winks and swings its antenna over to the other side. */
export function AgentsNavIcon({ className }: NavIconProps) {
  return (
    <NavIconSvg className={className}>
      <path d="M12 8V4" />
      <path className="nav-icon-antenna" d="M12 4H8" />
      <rect width="16" height="12" x="4" y="8" rx="2" />
      <path d="M2 14h2" />
      <path d="M20 14h2" />
      <path d="M15 13v2" />
      <path className="nav-icon-eye-open" d="M9 13v2" />
      <path className="nav-icon-eye-shut" d="M8 14h2" />
    </NavIconSvg>
  );
}

/** The two control knobs draw in toward each other. */
export function AdminNavIcon({ className }: NavIconProps) {
  return (
    <NavIconSvg className={className}>
      <path className="nav-icon-rail-lower" d="M14 17H5" />
      <path className="nav-icon-rail-upper" d="M19 7h-9" />
      <circle className="nav-icon-knob-lower" cx="17" cy="17" r="3" />
      <circle className="nav-icon-knob-upper" cx="7" cy="7" r="3" />
    </NavIconSvg>
  );
}
